/**
 * freeCourseProviders.js
 * --------------------------------------------------------------------------
 * Deep-link helpers for free / free-to-audit course providers.
 *
 * For each missing skill, the user can click through to a real search
 * results page on a real platform. The platform answers what's currently
 * available, we don't try to maintain a course list ourselves and we
 * don't fabricate links. Result quality is whatever the provider currently
 * has indexed.
 *
 * URL patterns last verified: April 2026. If a provider changes their
 * search URL, the click still navigates to the provider, worst case
 * the user sees the homepage and runs the search themselves.
 */

const enc = (s) => encodeURIComponent(s);

export const FREE_COURSE_PROVIDERS = [
  {
    id: 'classcentral',
    name: 'Class Central',
    descKey: 'employeePortal.profile.providerDesc.classcentral',
    icon: 'GraduationCap',
    color: 'bg-emerald-500',
    badgeKey: 'employeePortal.profile.providerBadge.aggregator',
    buildUrl: (query) => `https://www.classcentral.com/search?q=${enc(query)}`,
  },
  {
    id: 'coursera',
    name: 'Coursera',
    descKey: 'employeePortal.profile.providerDesc.coursera',
    icon: 'BookOpen',
    color: 'bg-blue-600',
    badgeKey: 'employeePortal.profile.providerBadge.freeAudit',
    buildUrl: (query) => `https://www.coursera.org/courses?query=${enc(query)}&price=1`,
  },
  {
    id: 'edx',
    name: 'edX',
    descKey: 'employeePortal.profile.providerDesc.edx',
    icon: 'BookOpen',
    color: 'bg-slate-700',
    badgeKey: 'employeePortal.profile.providerBadge.freeAudit',
    buildUrl: (query) => `https://www.edx.org/search?q=${enc(query)}&availability=Available%20now&availability=Upcoming`,
  },
  {
    id: 'youtube',
    name: 'YouTube',
    descKey: 'employeePortal.profile.providerDesc.youtube',
    icon: 'PlayCircle',
    color: 'bg-red-600',
    badgeKey: 'employeePortal.profile.providerBadge.free',
    buildUrl: (query) => `https://www.youtube.com/results?search_query=${enc(query + ' tutorial course')}`,
  },
  {
    id: 'mit-ocw',
    name: 'MIT OpenCourseWare',
    descKey: 'employeePortal.profile.providerDesc.mit',
    icon: 'School',
    color: 'bg-rose-600',
    badgeKey: 'employeePortal.profile.providerBadge.free',
    buildUrl: (query) => `https://ocw.mit.edu/search/?q=${enc(query)}`,
  },
  {
    id: 'freecodecamp',
    name: 'freeCodeCamp',
    descKey: 'employeePortal.profile.providerDesc.freecodecamp',
    icon: 'Code2',
    color: 'bg-emerald-700',
    badgeKey: 'employeePortal.profile.providerBadge.freeCert',
    buildUrl: (query) => `https://www.freecodecamp.org/news/search/?query=${enc(query)}`,
  },
];

/**
 * Returns the providers most likely to have content for a given skill
 * category. For technical skills (engineering / data) freeCodeCamp + MIT
 * make sense; for business skills they don't. Saves the worker from
 * clicking through to providers that won't have what they need.
 */
export function relevantProvidersFor(skillCategory = 'general') {
  // For now everyone gets the same six. Future: filter by category.
  // freeCodeCamp dropped for non-technical skills below.
  if (skillCategory === 'technical') return FREE_COURSE_PROVIDERS;
  // Drop freeCodeCamp + MIT OCW for business/HR skills (they're mostly tech).
  return FREE_COURSE_PROVIDERS.filter(
    (p) => p.id !== 'freecodecamp' && p.id !== 'mit-ocw'
  );
}

/** Map a skill ID to "technical" | "general" so we know which providers to show. */
const TECHNICAL_SKILL_IDS = new Set([
  'js', 'ts', 'react', 'vue', 'angular', 'node', 'python', 'java', 'go', 'rust',
  'sql', 'aws', 'gcp', 'azure', 'k8s', 'docker', 'cicd', 'systemDesign',
  'codeReview', 'analytics',
]);

export function categoryForSkill(skillId) {
  return TECHNICAL_SKILL_IDS.has(skillId) ? 'technical' : 'general';
}
